import {
  type DocumentReference,
  type INamedDocument,
  type ISoftDelete,
} from '@principle-theorem/shared';
import { type ITreatmentTemplateWithStep } from '../clinical-charting/treatment/treatment-plan-from-template';
import { type IDepositMetadata } from '../deposit/deposit-metadata';
import { type IDepositRequest } from '../deposit/deposit-request';
import { type DepositUnresolvedReason } from '../deposit/deposit-unresolved-reason';
import { type ICalendarEvent, isCalendarEvent } from '../event/calendar-event';
import { type IPatient } from '../patient/patient';
import {
  type IPatientFormData,
  type IPrimaryContactFormData,
} from '../patient/patient-scheduling';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';

/**
 * Deposit state persisted on an appointment request.
 *
 * `metadata` is absent when `unresolved` is set — a deposit was expected but no
 * amount could be derived, so there is no deposit to describe.
 */
export interface IAppointmentRequestDeposit {
  metadata?: IDepositMetadata;
  requestRef?: DocumentReference<IDepositRequest>;
  unresolved?: DepositUnresolvedReason;
}

export interface IAppointmentRequest extends ICalendarEvent, ISoftDelete {
  practitioner: INamedDocument<IStaffer>;
  practice: INamedDocument<IPractice>;
  patient: {
    primaryContact: IPrimaryContactFormData;
    patient?: IPatientFormData;
  };
  patientRef?: DocumentReference<IPatient>;
  template: ITreatmentTemplateWithStep;
  deposit?: IAppointmentRequestDeposit;
}

export function isAppointmentRequest(
  data: unknown
): data is IAppointmentRequest {
  return (
    isCalendarEvent(data) &&
    'patient' in data &&
    'practice' in data &&
    'practitioner' &&
    'template' in data
  );
}
